import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

import { postJson } from "./api";

type RegistrationStartResponse = {
  registration_id: string;
  username: string;
  display_name: string;
  user_id: string;
  rp_id: string;
  challenge: string;
  public_key?: unknown;
  status: "challenge";
};

type RegistrationFinishResponse = {
  status: "registered";
  credential_id: string;
  user_id: string;
  session_token?: string;
  token_purpose?: string;
  expires_in_seconds?: number;
  passkey_prf?: PasskeyPRFResult;
};

type AuthenticationStartResponse = {
  authentication_id: string;
  username: string;
  user_id: string;
  rp_id: string;
  challenge: string;
  public_key?: unknown;
  status: "challenge";
};

type AuthenticationFinishResponse = {
  status: "authenticated";
  session_token: string;
  credential_id: string;
  user_id: string;
  passkey_prf?: PasskeyPRFResult;
};

export type PasskeyPRFInputs = {
  first: BufferSource;
  second?: BufferSource;
};

export type PasskeyPRFResult = {
  first?: string;
  second?: string;
  enabled?: boolean;
  available: boolean;
};

function assertObject(value: unknown, name: string): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object") {
    throw new Error(`${name} missing from API response`);
  }
}

function base64URLFromBuffer(value: BufferSource): string {
  const bytes =
    value instanceof ArrayBuffer
      ? new Uint8Array(value)
      : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function prfOutputToBase64URL(value: unknown): string | undefined {
  if (value instanceof ArrayBuffer) {
    return base64URLFromBuffer(value);
  }
  if (ArrayBuffer.isView(value)) {
    return base64URLFromBuffer(value);
  }
  return undefined;
}

function clientPRFResult(clientExtensionResults: unknown): PasskeyPRFResult {
  const extensions =
    clientExtensionResults && typeof clientExtensionResults === "object"
      ? (clientExtensionResults as Record<string, unknown>)
      : {};
  const prf = extensions.prf && typeof extensions.prf === "object" ? (extensions.prf as Record<string, unknown>) : {};
  const results = prf.results && typeof prf.results === "object" ? (prf.results as Record<string, unknown>) : {};
  const first = prfOutputToBase64URL(results.first);
  const second = prfOutputToBase64URL(results.second);
  return {
    first,
    second,
    enabled: typeof prf.enabled === "boolean" ? prf.enabled : undefined,
    available: !!first || prf.enabled === true,
  };
}

function withPRFInputs(publicKey: unknown, prfInputs?: PasskeyPRFInputs): unknown {
  if (!prfInputs) {
    return publicKey;
  }
  assertObject(publicKey, "public_key");
  const extensions =
    publicKey.extensions && typeof publicKey.extensions === "object"
      ? (publicKey.extensions as Record<string, unknown>)
      : {};
  return {
    ...publicKey,
    extensions: {
      ...extensions,
      prf: {
        eval: {
          first: prfInputs.first,
          ...(prfInputs.second ? { second: prfInputs.second } : {}),
        },
      },
    },
  };
}

export function vaultUnlockPRFInputs(accountHint: string): PasskeyPRFInputs {
  const normalized = accountHint.trim().toLowerCase();
  const encoder = new TextEncoder();
  return {
    first: encoder.encode(`key.store:vault-unlock:v1:${normalized}`),
    second: encoder.encode(`key.store:vault-unlock:v2:${normalized}`),
  };
}

export async function createPasskey(options: {
  username: string;
  displayName: string;
  prfInputs?: PasskeyPRFInputs;
}): Promise<RegistrationFinishResponse> {
  const start = await postJson<RegistrationStartResponse>("/v1/passkeys/registration/start", {
    username: options.username,
    display_name: options.displayName,
  });

  assertObject(start.public_key, "public_key");
  const optionsJSON = withPRFInputs(start.public_key, options.prfInputs);
  const attestation = await startRegistration({ optionsJSON: optionsJSON as never });

  const finish = await postJson<RegistrationFinishResponse>("/v1/passkeys/registration/finish", {
    registration_id: start.registration_id,
    credential_id: attestation.id,
    raw_id: attestation.rawId,
    credential_type: attestation.type,
    client_data_json: attestation.response.clientDataJSON,
    attestation_object: attestation.response.attestationObject,
  });
  return { ...finish, passkey_prf: clientPRFResult(attestation.clientExtensionResults) };
}

export async function signInWithPasskey(options: {
  username: string;
  prfInputs?: PasskeyPRFInputs;
}): Promise<AuthenticationFinishResponse> {
  const start = await postJson<AuthenticationStartResponse>("/v1/passkeys/authentication/start", {
    username: options.username,
  });

  assertObject(start.public_key, "public_key");
  const optionsJSON = withPRFInputs(start.public_key, options.prfInputs);
  const assertion = await startAuthentication({ optionsJSON: optionsJSON as never });

  const finish = await postJson<AuthenticationFinishResponse>("/v1/passkeys/authentication/finish", {
    authentication_id: start.authentication_id,
    credential_id: assertion.id,
    raw_id: assertion.rawId,
    credential_type: assertion.type,
    client_data_json: assertion.response.clientDataJSON,
    authenticator_data: assertion.response.authenticatorData,
    signature: assertion.response.signature,
    user_handle: assertion.response.userHandle,
  });
  return { ...finish, passkey_prf: clientPRFResult(assertion.clientExtensionResults) };
}
